/**
 * Inline, actionable `422` messages for the bot-texts editor and the FAQ dialog (templates doc
 * §13.9). Both surfaces write through the same content API and get the same problem-detail codes,
 * so the mapping is shared rather than duplicated per screen.
 */

export interface TemplateSaveError {
  code: string;
  details?: unknown;
}

interface RetiredPlaceholder {
  name: string;
  replacement?: string;
}

const RETIRED_PLACEHOLDER_KEYS = new Set(['catalog_link', 'mini_app_url', 'delivery_eta']);

export function inline422Message(
  error: TemplateSaveError,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const details = (error.details ?? {}) as Record<string, unknown>;
  switch (error.code) {
    case 'content.key.unknown':
      return t('editor.errors.keyUnknown');
    case 'content.key.retired':
      return details.replacement
        ? t('editor.errors.keyRetired', { replacement: details.replacement })
        : t('editor.errors.keyRetiredNone');
    case 'content.answer-key.invalid':
      return t('faqDialog.answerKeyInvalid');
    case 'content.part.invalid':
      return t('editor.errors.embeddedTextOnly');
    case 'content.placeholder.unknown': {
      const unknown = (details.unknownPlaceholders as string[] | undefined) ?? [];
      const allowed = (details.allowedPlaceholders as string[] | undefined) ?? [];
      const retired = (details.retiredPlaceholders as RetiredPlaceholder[] | undefined) ?? [];
      const parts: string[] = [];
      if (unknown.length > 0) {
        parts.push(
          t('editor.errors.placeholders', {
            placeholders: unknown.map((name) => `{{${name}}}`).join(', '),
            allowed: allowed.join(', '),
          }),
        );
      }
      for (const retiredPlaceholder of retired) {
        const key = RETIRED_PLACEHOLDER_KEYS.has(retiredPlaceholder.name)
          ? retiredPlaceholder.name
          : 'generic';
        parts.push(
          t(`editor.errors.retiredPlaceholder.${key}`, { name: `{{${retiredPlaceholder.name}}}` }),
        );
      }
      return parts.join(' ');
    }
    case 'content.action.invalid': {
      const reason = details.reason as string | undefined;
      if (reason === 'ACTIONS_NOT_ALLOWED') return t('editor.errors.actions.ACTIONS_NOT_ALLOWED');
      if (reason === 'MARKUP_MIX') return t('editor.errors.actions.MARKUP_MIX');
      if (reason === 'DEEP_LINK_TARGET_UNKNOWN') {
        return t('editor.errors.actions.DEEP_LINK_TARGET_UNKNOWN', {
          targets: (details.allowedDeepLinkTargets as string[] | undefined)?.join(', ') ?? '',
        });
      }
      if (reason === 'MARKUP_CONFLICT') {
        const kind =
          (details.expectedMarkup as string) === 'REPLY'
            ? t('editor.errors.markupKind.REPLY')
            : t('editor.errors.markupKind.INLINE');
        return t('editor.errors.actions.MARKUP_CONFLICT', { kind });
      }
      return t('editor.errors.actions.generic');
    }
    default:
      return error.code;
  }
}
